import { useTranslate } from '../../application/i18n/LocaleProvider';
import useCountUp from '../../application/motion/useCountUp';

/**
 * How far this run is from the next legend (SPEC-010 D-25, SPEC-007 D-54).
 *
 * THE GAP, NOT THE TARGET. It used to be a line of caption in the corner of the
 * scoreboard reading "New legend at 4,600" — a number a reader has to subtract
 * their own score from before it means anything. This says how many points are
 * left, over a bar that fills as the run scores, so a reader who is two rounds
 * away can SEE that they are.
 *
 * AT THE VERY TOP OF THE SCREEN, above the score itself, and that is the right
 * place for the one thing here that is not about this round: it is the reason
 * the run is being played, so it is the first thing read and the last thing in
 * the way. Everything below it — score, question, cards, clock — is the round.
 *
 * THE NUMBER COUNTS DOWN AND WARMS UP AS IT GOES (D-65). It rolls on the same
 * hook the score rolls on, in the other direction, so the two instruments move
 * as one thing; and it is drawn grey while the car is far away and closer to the
 * ramp's own yellow the nearer it gets, so a reader can tell how close they are
 * without reading the number at all. That is the same job the bar beside it
 * does, said in the one channel a bar cannot use.
 *
 * NOTHING HERE FLASHES (D-63). Two earlier cuts of this band signalled a closing
 * gap rather than showing one — a line under the hero repeating the number where
 * the reader's eyes already were, and then a flash across the whole row when it
 * moved. The first made the hero a paragraph and put the gap in two places, so
 * neither was the place to look for it; the second made the quietest fact on the
 * screen the loudest thing on it. This is the reason the run is being played, not
 * an event in it, and the counting and the colour say so without asking to be
 * looked at.
 *
 * It draws nothing at all once the ladder is spent or the garage is complete,
 * which is the same rule the record follows: a zero here would be a bar nobody
 * set.
 *
 * `short` is null for two very different reasons and the caller has already
 * separated them — a null TARGET is a ladder with nothing left on it, where a
 * null gap against a real target is a car this run has already earned.
 */
export default function DuelLadder({ target = null, short = null, score = 0 }) {
    const t = useTranslate();
    // Counted DOWN, the way the score is counted up (D-65). Before the early
    // return, because a hook may not be conditional — and `useCountUp` hands
    // back anything that is not a number untouched, so a spent ladder costs a
    // roll to zero and nothing else.
    const counting = useCountUp(short ?? 0);

    if (target === null) {
        return null;
    }

    const isSecured = short === null;
    const share = target > 0 ? Math.min(Math.max(score, 0) / target, 1) : 0;

    return (
        <div
            className={`duel-prize${isSecured ? ' duel-prize--secured' : ''}`}
            style={{ '--prize-share': share }}
        >
            {/* Keyed on the gap, so the number ticks each time a round closes
                it. The bar beside it moves under a transition and a bar is a
                slow reading; the tick is what makes a round feel like it bought
                something. */}
            {/* ARIA-HIDDEN, with the settled sentence beside it. The number in
                here counts down over about four hundred milliseconds, and a
                reader who happened to land on this line during one would be read
                a figure that was true for a frame. The visible line is the
                decoration; the one under it is the fact.

                UNKEYED, because there is no longer an animation to replay on it:
                the figure inside it rolls, and the roll lives in the hook above
                rather than in a remount. */}
            <p className="duel-prize__caption" aria-hidden="true">
                {isSecured ? t('duel.prize.secured') : t('duel.prize.gap', { count: counting })}
            </p>

            <p className="duel-prize__announced visually-hidden">
                {isSecured ? t('duel.prize.secured') : t('duel.prize.gap', { count: short })}
            </p>

            {/* The same fact drawn. Decorative — the line beside it is the number
                in words, and a bar that announced itself would say it twice. */}
            <span className="duel-prize__track" aria-hidden="true">
                <span className="duel-prize__fill" />
            </span>

        </div>
    );
}
